import {
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { from, lastValueFrom, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CryptoService } from '@core/services/encryption/crypto.service';
import { SettingsService } from '@features/system/settings/services/settings.service';
import { UserPreferenceService } from '@core/services/user-preference/user-preference.service';
import { combineLatest, filter, take } from 'rxjs';
import { environment } from '@env/environment';

/**
 * Interceptor to handle Zero-Knowledge Encryption for patient data.
 * It encrypts outgoing requests and decrypts incoming responses if enabled.
 */
export const encryptionInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const isApiRequest =
    req.url.startsWith(environment.apiBaseUrl) ||
    req.url.startsWith(environment.apiAuthUrl) ||
    req.url.startsWith(environment.apiAccountUrl);

  if (!isApiRequest) {
    return next(req);
  }

  const router = inject(Router);
  const cryptoService = inject(CryptoService);
  const settingsService = inject(SettingsService);
  const userPreferenceService = inject(UserPreferenceService);

  // 1. Check if target is a patient-related endpoint

  // 1. Check if target is a patient-related or EMR endpoint
  if (
    !req.url.includes('/patient') &&
    !req.url.includes('/emr')
  ) {
    return next(req);
  }

  // Prevent infinite loop: do not intercept requests to get the policy itself
  if (req.url.includes('/patient/encryption-policy')) {
    return next(req);
  }

  // 2. Fetch Encryption Policy & Check Logic
  // We wait for the organization AND branch to be selected effectively ensuring we have the context
  // before asking for the policy, avoiding 403s.
  return combineLatest([
    userPreferenceService.selectedOrg$,
    userPreferenceService.selectedBranch$
  ]).pipe(
    filter(([org, branch]) => !!org && !!branch), // Wait until we have valid org AND branch
    take(1),
    switchMap(() => settingsService.getPatientEncryptionPolicy()),
    switchMap((policy) => {
      // Check if Zero-Knowledge is enabled (backend returns 'ZERO_KNOWLEDGE')
      const encModel = (policy.encryptionModel || '').toUpperCase().replace(/-/g, '_');
      const isZeroKnowledge = encModel === 'ZERO_KNOWLEDGE';

      if (!isZeroKnowledge) {
        return next(req);
      }

      // 3. Vault Check
      return from(cryptoService.isVaultInitialized()).pipe(
        switchMap((isVaultReady) => {
          if (!isVaultReady) {
            // Vault needed but not ready -> Trigger global setup modal
            cryptoService.triggerVaultSetup();
            // We return a "handled" error or empty observable to stop the original request
            // returning throwError ensures the caller knows it failed.
            return throwError(() => new Error('Vault setup required'));
          }

          // 4. Handle Request Encryption (POST/PUT/PATCH)

          const EXCLUDED_URLS = [
            '/patient/search',
            '/patient-visits/find-or-create',
            '/patient-visits/start-visit'
          ];

          const isExcluded = EXCLUDED_URLS.some(url => req.url.includes(url));

          if (
            ['POST', 'PUT', 'PATCH'].includes(req.method) &&
            req.body &&
            !isExcluded
          ) {
            return from(cryptoService.encryptPayload(req.body)).pipe(
              switchMap((encryptedBody) => {
                const clonedReq = req.clone({
                  body: {
                    ...encryptedBody,
                    isClientEncrypted: true,
                  },
                });
                return handleResponse(next, clonedReq, cryptoService);
              }),
            );
          }

          // 5. Handle Response Decryption (GET/etc OR Search API)
          return handleResponse(next, req, cryptoService);
        }),
      );
    }),
    catchError((err) => {
      // If settings fail to load or other errors, propagate
      return throwError(() => err);
    }),
  );
};

/**
 * Helper to handle the response decryption part
 */
function handleResponse(
  next: HttpHandlerFn,
  req: HttpRequest<unknown>,
  cryptoService: CryptoService,
) {
  return next(req).pipe(
    switchMap((event) => {
      // We only care about HttpResponse with a body
      if (event instanceof HttpResponse && event.body) {
        return from(cryptoService.decryptPayload(event.body)).pipe(
          map((decryptedBody) => {
            return event.clone({ body: decryptedBody });
          }),
        );
      }
      return of(event);
    }),
  );
}
